import * as React from "react";
import { css } from "@emotion/core";
import { useActor } from "@xstate/react";
import { useDropzone } from "react-dropzone";
import {
  Button,
  Callout,
  H3,
  ButtonGroup,
  Text,
  ProgressBar,
  Switch,
} from "@blueprintjs/core";
import { Spacer } from "components/common";
import ImageList from "./ImageList";
import UploadProgress from "./UploadProgress";
import { getStartDate, getEndDate } from "utils";
import { usePreventSleep } from "hooks/";
import { isValidCoordinates } from "utils/mapping/coords";

const StatusMessage = ({ content, show, ...props }) =>
  show() ? <Callout {...props}>{content}</Callout> : null;

const ImageUpload = ({
  survey,
  uploaderService,
  setUploadInProgress,
  center,
  setCenter,
  zoom,
  setZoom,
  autoComplete,
  setDate,
  setStartTime,
  setEndTime,
  timezone,
  showTimezoneAlert,
  showCoordinatesAlert,
}) => {
  const { canSleep, allowSleep, preventSleep } = usePreventSleep();
  const [state, send] = useActor(uploaderService);

  const autoOrderOnComplete = state.context.autoComplete;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files: File[]) => {
      const anyNew = files.some((file) => {
        const [name, extension] = file.name.split(".");
        const uid = `${name}-${file.lastModified}-${
          file.size
        }.${extension.toLowerCase()}`;

        const image = state.context.images[uid];

        if (image && image.uploaded) {
          return false;
        }

        return true;
      });

      if (anyNew) {
        send({ type: "PREPARE", files });
      }
    },
    accept: ["image/jpeg"],
  });

  const images = React.useMemo(
    () =>
      Object.values(state.context.images).sort((a, b) =>
        a.name > b.name ? 1 : a.name < b.name ? -1 : 0
      ),
    [state.context.images]
  );

  React.useEffect(() => {
    if (images.length > 0) {
      const startDate = getStartDate(images);
      setDate(startDate);
      setStartTime(startDate);
      setEndTime(getEndDate(images));
    }
  }, [images]);

  const missing = images.filter(
    (image) => image.uploaded === false && image.handle === undefined
  );
  const complete = images.filter((image) => image.uploaded === true);

  const allUploaded =
    state.matches("idle.fresh") && missing.length === 0 && images.length > 0;

  const feedback = {
    missing: {
      title: "Some Files Missing",
      intent: "warning",
      content: `You have attempted to upload ${images.length} images, but only ${complete.length} were successful. Please upload any missing images, or delete any images by selecting them and clicking on the "Delete Selected Images" button.`,
      show: () => state.matches("idle.fresh") && missing.length > 0,
    },
    upload: {
      title: "All Files Ready For Upload",
      intent: "success",
      content: `${images.length} files have been added to the browser, and are ready to be uploaded. Add more files or press the 'Click to Upload' button.`,
      show: () => state.matches("idle.pending") && missing.length === 0,
    },
    uploadWithMissing: {
      title: "Some Files Ready For Upload",
      intent: "warning",
      content:
        "Files have been added to the browser, and are ready to be uploaded. There are still missing files. Add more files or press the 'Click to Upload' button.",
      show: () => state.matches("idle.pending") && missing.length > 0,
    },
    complete: {
      title: "All Files Uploaded",
      intent: "success",
      content: `All ${images.length} files that were added to the browser have been uploaded successfully.`,
      show: () => allUploaded,
    },
  };

  const showingAny = Object.entries(feedback).some(([key, value]) =>
    value.show()
  );

  const setAutoOrderOnComplete = (value: boolean) =>
    send({ type: "AUTO_COMPLETE", autoComplete: value });

  React.useEffect(() => {
    if (state.matches("complete")) {
      autoComplete();
    }
  }, [state]);

  React.useEffect(() => {
    setUploadInProgress(
      state.matches("uploading.progress") || state.matches("adding")
    );
  }, [state]);

  React.useEffect(() => {
    if (autoOrderOnComplete) {
      if (timezone === null) {
        showTimezoneAlert(true);
        setAutoOrderOnComplete(false);
        return;
      }

      if (!isValidCoordinates(center[1], center[0])) {
        showCoordinatesAlert(true);
        setAutoOrderOnComplete(false);
        return;
      }
    }
  }, [
    autoOrderOnComplete,
    timezone,
    center,
    showTimezoneAlert,
    showCoordinatesAlert,
  ]);

  React.useEffect(() => {
    if (state.matches("uploading")) {
      const confirm = (event) => {
        event.returnValue = "";
      };

      const cancel = (event) => {
        send({ type: "CANCEL" });
      };

      window.addEventListener("beforeunload", confirm);
      window.addEventListener("unload", cancel);

      return () => {
        window.removeEventListener("beforeunload", confirm);
        window.addEventListener("unload", cancel);
      };
    }
  }, [state]);

  React.useEffect(() => {
    if (state.matches("idle.fresh")) {
      if (canSleep === false) {
        allowSleep();
      }
    }
  }, [state]);

  const removeImages = React.useCallback((uids) => {
    if (uids.length > 0) {
      send({ type: "REMOVE", uids });
    }
  }, []);

  return (
    <div
      css={css`
        display: flex;
        width: 100%;
      `}
    >
      <div
        css={css`
          min-width: 380px;
          width: 30%;
          margin-right: 1rem;
        `}
      >
        {!state.matches("uploading") && !state.matches("adding") && (
          <div
            css={css`
              height: ${showingAny ? "auto" : "100%"};
            `}
            {...getRootProps()}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px dashed var(--neutral-6);
                border-radius: 0.25rem;
                padding: 1rem;
                height: ${showingAny ? "auto" : "100%"};

                &:hover {
                  background-color: var(--neutral-6);
                  cursor: pointer;
                }
              `}
            >
              <input {...getInputProps()} />
              <ButtonGroup fill>
                <Button icon="upload" large intent="primary">
                  {images.length === 0
                    ? "Click to Choose Files"
                    : "Click to Choose More Files"}
                </Button>
              </ButtonGroup>
              <Spacer />
              <Text>Files can be dragged and dropped on to this area.</Text>
            </div>
          </div>
        )}
        <Spacer />
        {state.matches("uploading") && (
          <>
            <UploadProgress
              images={state.context.images}
              progress={state.context.progress}
              showSingle={false}
            />
            <UploadProgress
              images={state.context.images}
              progress={state.context.progress}
              showAll={false}
            />
          </>
        )}
        {Object.entries(feedback).map(([key, f]) => (
          <StatusMessage key={key} {...f} />
        ))}
        <Spacer />
        {state.matches("idle.pending") && (
          <>
            <div>
              <Switch
                label="Automatically submit order when upload is completed."
                checked={autoOrderOnComplete}
                onChange={(event) =>
                  setAutoOrderOnComplete(event.target.checked)
                }
              />
              <Spacer size={0.5} />
              <Button
                size="large"
                fill
                onClick={() => {
                  send({ type: "UPLOAD" });
                  if (canSleep) {
                    preventSleep();
                  }
                }}
                intent="success"
              >
                Click to Upload
              </Button>
            </div>
          </>
        )}
        {state.matches("adding") && (
          <div
            css={css`
              display: flex;
              justify-content: center;
            `}
          >
            <div
              css={css`
                width: 75%;
                display: flex;
                flex-direction: column;
                align-items: center;
              `}
            >
              <H3>
                Added {state.context.adding.added}/{state.context.adding.total}{" "}
                files to the browser for upload.
              </H3>
              <Spacer />
              {state.context.adding.total && (
                <ProgressBar
                  intent="success"
                  value={
                    state.context.adding.added / state.context.adding.total
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
      <div
        css={css`
          flex-grow: 1;
        `}
      >
        <ImageList
          images={images}
          removeImages={removeImages}
          survey={survey}
          uploadInProgress={state.matches("uploading.progress")}
          center={center}
          setCenter={setCenter}
          zoom={zoom}
          setZoom={setZoom}
        />
      </div>
    </div>
  );
};

export default ImageUpload;
